/**
 * Workspace version-history panel.
 *
 * Presentational and PROPS-DRIVEN: every input arrives as a prop so the
 * functional stories can drive each state (timeline / empty / unsaved badge /
 * restoring) without a backend and without a mock. Production wires the props
 * from the version-history hooks in `ActiveWorkspaceCloudDetail`.
 *
 * NEVER-GIT CONTRACT: no user-visible string in this file —
 * or in `RestoreVersionDialog` — may contain commit, branch, log, checkout,
 * revert, reset, HEAD or dirty. Versions are "versions", the working delta is
 * "unsaved changes", and the version id is an opaque handle that is TRUNCATED
 * for display only and never labelled a hash. The `no git vocabulary in the
 * panel` story machine-checks this against the rendered DOM.
 */

import { useState } from 'react';
import { Button } from '@localagent/ui/components/button';
import type { VersionEntry } from '@localagent/sdk';
import { RestoreVersionDialog } from './RestoreVersionDialog';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

/** Display length of a version id. Presentation only — never the stored id. */
const VERSION_ID_DISPLAY_LENGTH = 12;

/** Shorten a version id for display. The full id is what gets restored. */
export function shortVersionId(version: string): string {
  return version.slice(0, VERSION_ID_DISPLAY_LENGTH);
}

/**
 * A coarse "how long ago" label. Deliberately coarse — the timeline is for
 * orientation, not forensics, and an exact clock time would be noise.
 */
export function relativeTime(timestamp: string, now: Date = new Date()): string {
  const then = new Date(timestamp);
  const elapsedMs = now.getTime() - then.getTime();
  if (Number.isNaN(elapsedMs)) {
    throw new Error(`version timestamp is not a valid date: ${timestamp}`);
  }

  const seconds = Math.floor(elapsedMs / 1000);
  if (seconds < 60) return 'just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;

  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

export interface VersionHistoryPanelProps {
  /** The version timeline, newest first. */
  entries: VersionEntry[];
  isLoading?: boolean;
  /** A load failure — surfaced, never swallowed into an empty timeline. */
  error?: string | null;
  /** Files differing from the last saved version. Count only. */
  unsavedCount?: number;
  /** Paths offered by the dialog's optional file picker. */
  availablePaths?: string[];
  /** A restore is in flight — every restore action is disabled. */
  isRestoring?: boolean;
  /** `paths` undefined ⇒ restore the whole workspace. */
  onRestore: (version: string, paths?: string[]) => void;
}

export function VersionHistoryPanel({
  entries,
  isLoading = false,
  error = null,
  unsavedCount = 0,
  availablePaths = [],
  isRestoring = false,
  onRestore,
}: VersionHistoryPanelProps) {
  const [pendingEntry, setPendingEntry] = useState<VersionEntry | null>(null);

  return (
    <div className="rounded-lg bg-card p-4" data-testid="version-history-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Version history</h3>
        {unsavedCount > 0 && (
          <span
            className="rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
            data-testid="unsaved-changes-badge"
          >
            {unsavedCount} unsaved change{unsavedCount === 1 ? '' : 's'} since last version
          </span>
        )}
      </div>

      {error && (
        <p className="mt-3 text-sm text-destructive" data-testid="version-history-error">
          Could not load version history: {error}
        </p>
      )}

      {!error && isLoading && (
        <LoadingState data-testid="version-history-loading" label="Loading versions…" className="mt-3" />
      )}

      {!error && !isLoading && entries.length === 0 && (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="version-history-empty">
          No versions yet.
        </p>
      )}

      {!error && !isLoading && entries.length > 0 && (
        <ul className="mt-3" data-testid="version-history-list">
          {entries.map((entry) => (
            <li
              key={entry.version}
              className="flex items-center justify-between gap-3 py-2"
              data-testid="version-entry"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium" data-testid="version-entry-message">
                  {entry.message}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  <span data-testid="version-entry-time">{relativeTime(entry.timestamp)}</span>
                  <span className="ml-2 font-mono" data-testid="version-entry-id">
                    {shortVersionId(entry.version)}
                  </span>
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                disabled={isRestoring}
                data-testid={`restore-version-${shortVersionId(entry.version)}`}
                onClick={() => setPendingEntry(entry)}
              >
                Restore
              </Button>
            </li>
          ))}
        </ul>
      )}

      <RestoreVersionDialog
        open={pendingEntry !== null}
        entry={pendingEntry}
        availablePaths={availablePaths}
        isRestoring={isRestoring}
        onClose={() => setPendingEntry(null)}
        onRestore={(version, paths) => {
          onRestore(version, paths);
          setPendingEntry(null);
        }}
      />
    </div>
  );
}
